import { AISummary } from '@statsbomb/kitbag-ai-summary';
import { Card } from '@statsbomb/kitbag-components';
import { useAtomValue } from 'jotai';
import { unwrap } from 'jotai/utils';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { playerIdAtom } from '@/atoms/player/player';
import { playerAiSummaryFilterParamsAtom } from '@/atoms/queries/player/playerAiSummaryFilters';
import { unwrappedSelectedSummaryProfileAtom } from '@/atoms/radar';
import { CardHeightWrapper } from '@/components/card/CardHeightWrapper';
import { NoTextLoader } from '@/components/contentState/NoTextLoader';
import { PLAYER_ID } from '@/consts/searchKeys';
import { usePlayerFilteredSummaryData } from '@/hooks/usePlayerFilteredSummaryData';
import { isNullish } from '@/utils/predicates';
import { toProfileInfo } from '@/utils/radar';

const unwrappedPlayerAiSummaryFilterParamsAtom = unwrap(playerAiSummaryFilterParamsAtom, () => undefined);

export const PlayerPreviewSummary = () => {
  const { t } = useTranslation('radar');
  const [searchParams] = useSearchParams();
  const urlPlayerId = searchParams.get(PLAYER_ID);
  const playerId = useAtomValue(playerIdAtom);
  const filterParams = useAtomValue(unwrappedPlayerAiSummaryFilterParamsAtom);
  const summaryData = usePlayerFilteredSummaryData();
  const selectedProfile = useAtomValue(unwrappedSelectedSummaryProfileAtom);

  // playerIdAtom is synced from the URL via useEffect in SetPlayerPreviewData, which fires
  // after paint. Show the spinner immediately when the URL changes, before the atom catches up.
  const isPlayerSyncing = playerId === null || String(playerId) !== urlPlayerId;

  if (!urlPlayerId) return null;

  // `selectedProfile` is undefined while the profile atom resolves, and null if it resolved but no
  // template matched the selected id — the same undefined/null distinction filterParams uses below.
  const isProfilePending = selectedProfile === undefined;
  const isLoading = isPlayerSyncing || filterParams === undefined || summaryData === undefined || isProfilePending;
  if (isLoading) return <NoTextLoader />;
  if (!filterParams)
    throw new Error('playerAiSummaryFilterParamsAtom resolved to null: no player cycles match selected filters');
  if (!selectedProfile)
    throw new Error('selectedSummaryProfileAtom resolved to null: no summary profile matches the selected id');

  const profileInfo = toProfileInfo(selectedProfile, t, summaryData.positionMinutes, summaryData.hiddenMetricKeys);

  const playerData = {
    playerId: String(playerId),
    metrics: summaryData.metrics,
    minutesPlayed: summaryData.minutesPlayed,
    primaryPosition: summaryData.primaryPosition,
    positions: summaryData.positions,
    ...(!isNullish(summaryData.cohortSize) ? { cohortSize: summaryData.cohortSize } : {}),
    competitionInfos: filterParams.competitionInfos.map(({ id, name, country }) => ({
      id: String(id),
      name,
      country,
    })),
    seasonInfos: filterParams.seasonInfos.map(({ id, name }) => ({
      id: String(id),
      name,
    })),
    profileInfo,
  };

  return (
    <div className="h-full max-md:h-[75vh] max-md:flex-none md:flex-1 md:min-h-0">
      <Card>
        <CardHeightWrapper>
          {/* Required structural layer: CardHeightWrapper targets [&>div] and [&>div>div] — do not remove */}
          <div>
            <AISummary key={playerData.playerId} playerData={playerData} />
          </div>
        </CardHeightWrapper>
      </Card>
    </div>
  );
};
